import { Badge } from "@/components/ui/badge";
import PageLayout from "@/components/layout/PageLayout";
import PageBreadcrumb from "@/components/layout/PageBreadcrumb";
import { Building2, Users2, Target, Award, UserRound } from "lucide-react";
import Cities from "@/components/sections/Cities";

const AboutPage = () => {
  return (
    <PageLayout>
      <PageBreadcrumb currentPage="About Us" />
      
      {/* Hero Section */}
      <section className="w-full py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4 animate-fade-up">About Us</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-dark mb-6 animate-fade-up [animation-delay:200ms]">
              Your Trusted Insurance <span className="text-primary">Partner</span>
            </h1>
            <p className="text-lg text-neutral mb-8 animate-fade-up [animation-delay:400ms]">
              At Kover King, we're dedicated to protecting what matters most to you. With years of experience and a commitment to excellence, we provide comprehensive insurance solutions tailored to your unique needs.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="w-full py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="animate-fade-up">
                <Badge variant="secondary" className="bg-primary/10 text-primary mb-4">
                  Our Mission
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6">
                  Making Insurance Simple and Accessible
                </h2>
                <p className="text-neutral mb-6">
                  We believe everyone deserves access to quality insurance coverage without the complexity. Our mission is to simplify the insurance process while providing comprehensive protection for families and businesses across Illinois.
                </p>
                <div className="space-y-4">
                  {[
                    "Personalized insurance solutions",
                    "Expert guidance and support",
                    "Fast, hassle-free claims process",
                    "Competitive rates and flexible plans"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Award className="text-primary h-5 w-5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-up [animation-delay:200ms]">
                <div className="aspect-square rounded-xl bg-primary/10 flex items-center justify-center">
                  <Target className="h-32 w-32 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="w-full py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4 animate-fade-up">Our Values</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6 animate-fade-up [animation-delay:200ms]">
                What Sets Us Apart
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Building2 className="h-8 w-8" />,
                  title: "Trust & Reliability",
                  description: "Built on a foundation of trust and backed by years of industry experience."
                },
                {
                  icon: <Users2 className="h-8 w-8" />,
                  title: "Customer-First Approach",
                  description: "Your needs and satisfaction are at the heart of everything we do."
                },
                {
                  icon: <Award className="h-8 w-8" />,
                  title: "Excellence in Service",
                  description: "Committed to providing outstanding service and support at every step."
                }
              ].map((value, index) => (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow animate-fade-up"
                  style={{ animationDelay: `${400 + (index * 200)}ms` }}
                >
                  <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-neutral">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="w-full py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4 animate-fade-up">Our Team</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6 animate-fade-up [animation-delay:200ms]">
                Leadership Team
              </h2>
              <p className="text-neutral max-w-2xl mx-auto animate-fade-up [animation-delay:400ms]">
                Meet the dedicated leaders behind Kover King who are committed to providing exceptional insurance solutions.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  name: "Doug Allen",
                  role: "Chief Executive Officer",
                  description: "Leading Kover King with vision and expertise in the insurance industry."
                },
                {
                  name: "Rhonda Allen",
                  role: "Vice President",
                  description: "Driving operational excellence and customer satisfaction across our services."
                }
              ].map((member, index) => (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow animate-fade-up"
                  style={{ animationDelay: `${600 + (index * 200)}ms` }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="h-24 w-24 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                      <UserRound className="h-12 w-12 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-primary-dark mb-2">{member.name}</h3>
                    <p className="text-primary mb-4 font-medium">{member.role}</p>
                    <p className="text-neutral">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Cities />
    </PageLayout>
  );
};

export default AboutPage;
